import axios from "axios";
import {
  Opportunity, OpportunityListResponse, Feedback, FeedbackResponse,
  ProposalResponseListResponse, ResponseTemplate,
  SaveResponseRequest, SaveResponseResult,
  OpportunityDocumentsResponse,
  OpportunityDocument,
  RfpAnalysis,
} from "./types";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:7071/api";

let _authToken: string = "";
let _tokenRefresher: (() => Promise<string>) | null = null;

export function setAuthToken(token: string) {
  _authToken = token;
}

export function setTokenRefresher(fn: () => Promise<string>) {
  _tokenRefresher = fn;
}

async function authHeaders(): Promise<Record<string, string>> {
  if (_tokenRefresher) {
    try {
      _authToken = await _tokenRefresher();
    } catch {
      // Fall through with existing token
    }
  }
  return { Authorization: `Bearer ${_authToken}` };
}

export async function getOpportunities(
  status: string = "new",
  limit: number = 50,
  skip: number = 0
): Promise<OpportunityListResponse> {
  const headers = await authHeaders();
  const response = await axios.get(`${API_BASE}/gov/opportunities`, {
    headers,
    params: { status, limit, skip },
  });
  return response.data;
}

export async function getOpportunity(id: string): Promise<Opportunity> {
  const headers = await authHeaders();
  const response = await axios.get(`${API_BASE}/gov/opportunities/${id}`, {
    headers,
  });
  return response.data;
}

export async function submitFeedback(feedback: Feedback): Promise<FeedbackResponse> {
  const headers = await authHeaders();
  const response = await axios.post(`${API_BASE}/gov/feedback`, feedback, {
    headers,
  });
  return response.data;
}

// ── Response Workspace ──────────────────────────────────────────────────────

export async function getResponses(opportunityId: string): Promise<ProposalResponseListResponse> {
  const headers = await authHeaders();
  const response = await axios.get(`${API_BASE}/gov/responses`, {
    headers,
    params: { opportunity_id: opportunityId },
  });
  return response.data;
}

export async function saveResponse(data: SaveResponseRequest): Promise<SaveResponseResult> {
  const headers = await authHeaders();
  const response = await axios.post(`${API_BASE}/gov/responses`, data, {
    headers,
  });
  return response.data;
}

export async function updateResponse(
  responseId: string,
  data: Partial<SaveResponseRequest>
): Promise<SaveResponseResult> {
  const headers = await authHeaders();
  const response = await axios.put(`${API_BASE}/gov/responses/${responseId}`, data, {
    headers,
  });
  return response.data;
}

export async function getTemplates(): Promise<ResponseTemplate[]> {
  const headers = await authHeaders();
  const response = await axios.get(`${API_BASE}/gov/templates`, {
    headers,
  });
  return response.data;
}

// ── Opportunity Documents ────────────────────────────────────────────────────

export async function getDocuments(opportunityId: string): Promise<OpportunityDocumentsResponse> {
  const headers = await authHeaders();
  const response = await axios.get(`${API_BASE}/gov/opportunities/${opportunityId}/documents`, {
    headers,
  });
  return response.data;
}

export async function uploadDocument(
  opportunityId: string,
  file: File,
  documentType: string = "attachment"
): Promise<{ document: OpportunityDocument }> {
  const headers = await authHeaders();
  const formData = new FormData();
  formData.append("file", file);
  formData.append("document_type", documentType);
  const response = await axios.post(
    `${API_BASE}/gov/opportunities/${opportunityId}/documents/upload`,
    formData,
    { headers: { ...headers, "Content-Type": "multipart/form-data" } },
  );
  return response.data;
}

// ── RFP Analysis ─────────────────────────────────────────────────────────────

export async function analyzeRfp(
  opportunityId: string
): Promise<{ status: string; analysis: RfpAnalysis }> {
  const headers = await authHeaders();
  const response = await axios.post(
    `${API_BASE}/gov/rfp-analysis`,
    { opportunity_id: opportunityId },
    { headers },
  );
  return response.data;
}

export async function getRfpAnalysis(
  opportunityId: string
): Promise<{ status: string; analysis: RfpAnalysis | null }> {
  const headers = await authHeaders();
  const response = await axios.get(
    `${API_BASE}/gov/rfp-analysis/${opportunityId}`,
    { headers },
  );
  return response.data;
}

// ── Branding (public — no auth required) ────────────────────────────────────

export interface BrandingConfig {
  header_logo_url: string | null;
  login_logo_url: string | null;
}

export async function getBranding(): Promise<BrandingConfig> {
  const response = await axios.get(`${API_BASE}/gov/branding`);
  return response.data;
}
